import {rightCurry} from "../global/functionalTools";
import {PartSelectableObject} from "./partSelOb";
import {THREE} from "./resource/BVH.js";
import {Vector2} from "./delme_globals";


export class DrehWinkelHelper {
    private static sign(p1:THREE.Vector2, p2:THREE.Vector2, p3:THREE.Vector2)
    {
        return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    }

    private static pointIsInTriangle (pt:THREE.Vector2, v1:THREE.Vector2,  v2:THREE.Vector2, v3:THREE.Vector2):boolean
    {
        const d1 = DrehWinkelHelper.sign(pt, v1, v2);
        const d2 = DrehWinkelHelper.sign(pt, v2, v3);
        const d3 = DrehWinkelHelper.sign(pt, v3, v1);
        const has_neg = (d1 < 0) || (d2 < 0) || (d3 < 0);
        const has_pos = (d1 > 0) || (d2 > 0) || (d3 > 0);
        return !(has_neg && has_pos);
    }

    public static triangleContainsPointInArray(uvPoints, pointsToCheck:Array<Vector2>):boolean {
        return pointsToCheck.reduce(function(carry:boolean, uv:Vector2) {
            if(carry === true) {
                return true;
            }
            return DrehWinkelHelper.pointIsInTriangle(new THREE.Vector2(uv.x, uv.y), uvPoints[0], uvPoints[1], uvPoints[2]);
        }, false);
    }
}

export class DrehwinkelAufSelObErmittler {

    private static averageVectors(vectorArray:Array<THREE.Vector3>):THREE.Vector3 {
        if(vectorArray.length === 0) {
            return new THREE.Vector3();
        }
        return vectorArray.reduce(function(output, vector) {
            return output.add(vector)
        }, new THREE.Vector3()).multiplyScalar(1/vectorArray.length);
    }

    private static isInBounds = (number, edgeToBeRemoved)=>number > 0+edgeToBeRemoved && number < 1 - edgeToBeRemoved;
    private static removeOutOfBoundsUvs(uvArray:Array<Vector2>, edgeToBeRemoved=0):Array<Vector2> {
        return uvArray.filter(uv=>DrehwinkelAufSelObErmittler.isInBounds(uv.x, edgeToBeRemoved) && DrehwinkelAufSelObErmittler.isInBounds(uv.y, edgeToBeRemoved));
    }
    private static removeUvPointsAdEdge = rightCurry(DrehwinkelAufSelObErmittler.removeOutOfBoundsUvs, 0.05);

    public static getDrehWinkel(partSelOb:PartSelectableObject) {
        const self = DrehwinkelAufSelObErmittler;
        const inBoundsUVs = self.removeOutOfBoundsUvs(
            [partSelOb.translation, ...partSelOb.corners].map(p=>self.getRelFromAbs(p, partSelOb.getParent().textureSize))
        );
        const UvsToCheckWithoutEdgePoints = self.removeUvPointsAdEdge(inBoundsUVs);
        const UVsToCheck = UvsToCheckWithoutEdgePoints.length > 0 ? UvsToCheckWithoutEdgePoints : inBoundsUVs;
        let mesh = partSelOb.getParent().mesh as THREE.Mesh;
        let normalVectors = self.getFacesAtUVs(mesh.geometry, UVsToCheck).map(f=>f.normal);
        const morePosThanNeg= self.getIsMorPositiveNormalsInZCoordinateThanNegative(mesh)
        if(normalVectors.length === null) {
            return NaN;
        }
        const zCoordinatesOfNormalVectorsHaveAllSameSign = normalVectors.reduce((carry: { lastValue:number,  allSameSign:boolean}, v:THREE.Vector3)=>{
            if(!carry.allSameSign) {
                return carry;
            }
            if(carry.lastValue === Infinity || Math.sign(v.z) === 0) {
                carry.lastValue = v.z;
                return carry;
            }
            if(Math.sign(carry.lastValue) === Math.sign(v.z)) {
                carry.lastValue = v.z;
                return carry;
            }
            carry.allSameSign = false;
            return carry;
        }, {lastValue:Infinity, allSameSign:true}).allSameSign

        if(!zCoordinatesOfNormalVectorsHaveAllSameSign) {
            let operation:(v:number)=>number;
            if (morePosThanNeg) {
                operation = s => Math.abs(s);
            } else {
                operation = s => -Math.abs(s);
            }
            normalVectors = normalVectors.map(v=>{
                v.z = operation(v.z);
                return v;
            });
        }

        return self.getDrehWinkelRadFromNormalenVector(mesh.localToWorld(self.averageVectors(normalVectors)));
    }

    private static getRelFromAbs(absValue:Vector2, toBeSetInRelationTo:Vector2):Vector2 {
        return new Vector2(absValue.x / toBeSetInRelationTo.x, absValue.y / toBeSetInRelationTo.y);
    }

    public static getIsMorPositiveNormalsInZCoordinateThanNegative(mesh:THREE.Mesh) {
        const faceIterator = FaceIteratorFactory.getIterator(mesh.geometry);
        let pos = 0;
        let neg = 0;
        let responseWithFace = faceIterator.next();
        while (responseWithFace  !== null) {
            if(responseWithFace.face.normal.z>0) {
                pos++;
            } else if(responseWithFace.face.normal.z < 0){
                neg++;
            }
            responseWithFace = faceIterator.next();
        }
        return pos>neg;
    }


    private static getFacesAtUVs(geometry, uvArray:Array<Vector2>):Array<THREE.Face3> {
        const faceIterator = FaceIteratorFactory.getIterator(geometry);
        const out = [] as any[];
        let pos = 0;
        let neg = 0;
        let responseWithFace = faceIterator.next();
        while (responseWithFace  !== null && out.length < uvArray.length) {
            if(responseWithFace.face.normal.z>0) {
                pos++;
            } else if(responseWithFace.face.normal.z < 0){
                neg++;
            }
            if(DrehWinkelHelper.triangleContainsPointInArray(responseWithFace.uvPoints, uvArray)) {
                out.push(responseWithFace.face);
            }
            responseWithFace = faceIterator.next();
        }
        return out;
    }

    private static getDrehWinkelRadFromNormalenVector(normalenVectorWorldSpace:THREE.Vector3) {
        let winkel = Math.atan(-1 * normalenVectorWorldSpace.x / normalenVectorWorldSpace.z);
        const zkomponente = -1 * normalenVectorWorldSpace.x * Math.sin(winkel) + normalenVectorWorldSpace.z * Math.cos(winkel);
        if(zkomponente < 0) {
            winkel =winkel - Math.PI;
        }
        return winkel;
    }
}


interface FaceIterator {
    next();
}

export class FaceIteratorFactory {
    public static getIterator(geometry: THREE.Geometry|THREE.BufferGeometry):FaceIterator {
        if (geometry instanceof THREE.BufferGeometry) {
            if(geometry.index) {
                return new IndexedBufferGeometryFaceIterator(geometry);
            }
            return new BufferGeometryFaceIteratorWithoutIndizes(geometry);
        }
        return new UnBufferedGeometryFaceIterator(geometry);
    }
}

export class FaceIteratorResponse {
    public face: THREE.Face3;
    public uvPoints: THREE.Vector2[];
    public faceIndex: number;

    public constructor(face, uvPoints, index) {
        this.face  =face;
        this.uvPoints = uvPoints;
        this.faceIndex = index;
    }

}

export class IndexedBufferGeometryFaceIterator implements FaceIterator{

    private groupIndex = 0;
    private readonly geometry: THREE.BufferGeometry;
    private indexOfFaceInGroup = -3;


    public constructor(geometry:THREE.BufferGeometry) {
        this.geometry = geometry;
    }


    private countupIndizes() {

        this.indexOfFaceInGroup +=3;
        if(this.indexOfFaceInGroup + 2 > this.geometry.groups[this.groupIndex].count) {
            this.indexOfFaceInGroup = 0;
            this.groupIndex++;
        }
    }

    private static initGroupsIfEmpty(geometry:THREE.BufferGeometry) {
        if (geometry.groups.length === 0) {
            geometry.groups = [{start: 0, count: geometry.index.array.length, index: 0}];
        }
    }

    public next() {
        IndexedBufferGeometryFaceIterator.initGroupsIfEmpty(this.geometry);
        this.countupIndizes();
        if(typeof(this.geometry.groups[this.groupIndex]) === "undefined") {
            return null;
        }
        return IndexedBufferGeometryFaceIterator.getFace(this.geometry, this.indexOfFaceInGroup);
    }

    private static getFace(geometry, indexOfFaceInGroup) {
        const vA = new THREE.Vector3();
        const vB = new THREE.Vector3();
        const vC = new THREE.Vector3();
        const uvA = new THREE.Vector2();
        const uvB = new THREE.Vector2();
        const uvC = new THREE.Vector2();
        let uvs;


        const attributes = geometry.attributes;


        const indices = geometry.index.array;

        const positions = attributes.position.array;

        const a = (indices[indexOfFaceInGroup]);
        const b = (indices[indexOfFaceInGroup + 1]);
        const c = (indices[indexOfFaceInGroup + 2]);

        vA.fromArray(positions, a * 3);
        vB.fromArray(positions, b * 3);
        vC.fromArray(positions, c * 3);


        const tmpFace = new THREE.Face3(a, b, c, THREE.Triangle.normal(vA, vB, vC));

        if (attributes.uv !== undefined) {
            uvs = attributes.uv.array;
            uvA.fromArray(uvs, a * 2);
            uvB.fromArray(uvs, b * 2);
            uvC.fromArray(uvs, c * 2);
        }
        return new FaceIteratorResponse(tmpFace, [uvA, uvB, uvC], indexOfFaceInGroup);
    }


}

export class BufferGeometryFaceIteratorWithoutIndizes implements FaceIterator{
    private indexOfFaceInArray = -9;
    private readonly geometry: THREE.BufferGeometry;

    public constructor(geometry:THREE.BufferGeometry) {
        this.geometry = geometry;
    }

    private countupIndizes() {
        this.indexOfFaceInArray +=9;
    }

    public next() {
        this.countupIndizes();
        return BufferGeometryFaceIteratorWithoutIndizes.getFace(this.geometry, this.indexOfFaceInArray);
    }

    private static getFace(geometry:THREE.BufferGeometry, faceIndexInArray) {
        const vA = new THREE.Vector3();
        const vB = new THREE.Vector3();
        const vC = new THREE.Vector3();
        const uvA = new THREE.Vector2();
        const uvB = new THREE.Vector2();
        const uvC = new THREE.Vector2();

        let uvs;
        const faceIndex = 0;

        const attributes = geometry.attributes;


        const positions = attributes.position.array;
        if(faceIndexInArray +9 > positions.length - 1) {
            return null;
        }


        vA.fromArray(positions, faceIndexInArray);
        vB.fromArray(positions, faceIndexInArray + 3);
        vC.fromArray(positions, faceIndexInArray + 6);

        const tmpFace = new THREE.Face3(faceIndex, faceIndex+3, faceIndex+9, THREE.Triangle.normal(vA, vB, vC));

        if (attributes.uv !== undefined) {
            uvs = attributes.uv.array;
            uvA.fromArray(uvs, (faceIndexInArray * 3) * 2);
            uvB.fromArray(uvs, (faceIndexInArray * 3 + 1) * 2);
            uvC.fromArray(uvs, (faceIndexInArray * 3 + 2) * 2);
        }
        return new FaceIteratorResponse(tmpFace, [uvA, uvB, uvC], faceIndexInArray);
    }

}

export class UnBufferedGeometryFaceIterator implements FaceIterator{
    private readonly geometry: THREE.Geometry;
    private faceIndex = -1;
    // ignoriert Morphtargets!

    public constructor(geometry: THREE.Geometry) {
        this.geometry = geometry;
    }

    public next() {
        this.faceIndex ++;
        if(this.faceIndex > this.geometry.faces.length-1) {
            return null;
        }
        return UnBufferedGeometryFaceIterator.getFace(this.geometry, this.faceIndex);
    }

    private static getFace(geometry:THREE.Geometry, faceIndex:number) {
        const uvA = new THREE.Vector2();
        const uvB = new THREE.Vector2();
        const uvC = new THREE.Vector2();

        const face = geometry.faces[faceIndex];
        if (geometry.faceVertexUvs[0].length > 0) {
            const uvs = geometry.faceVertexUvs[0][0];
            uvA.copy(uvs[faceIndex * 3]);
            uvB.copy(uvs[faceIndex * 3 + 1]);
            uvC.copy(uvs[faceIndex * 3 + 2]);

        }
        return new FaceIteratorResponse(face, [uvA, uvB, uvC], faceIndex);
    }
}
